/**
 * A fund's disclosed trades — the vocabulary the transactions history answers
 * in, and how one of them is drawn on a chart.
 *
 * The backend's `action` is judged on share count (`basis` says when it had to
 * fall back to weight), so it is the one field that says which way a trade
 * went. Weight change is not: a position sold into a rising price ends the
 * month a larger share of the fund, and on the fund sampled a third of rows had
 * the two moving in opposite directions.
 */
import { pct, signedPct } from './format';

/** A change's own vocabulary → the tone and the word a reader wants. */
export const TRADE_ACTION = {
  ADDED: { label: 'New position', tone: 'green' },
  EXITED: { label: 'Sold out', tone: 'red' },
  INCREASED: { label: 'Bought more', tone: 'green' },
  REDUCED: { label: 'Trimmed', tone: 'amber' },
};

/** Whether the desk ended the period holding more of the name. */
export const isBuy = (action) => action === 'ADDED' || action === 'INCREASED';

/**
 * One trade as a dot on a series.
 *
 * Dated on the disclosure it was read from: a filing says what the book looked
 * like at month end, not which day the desk dealt, so the dot sits on the last
 * price at or before that date and the tooltip names the window it fell in.
 */
export function tradeMarker(period, change) {
  const action = TRADE_ACTION[change.action] ?? { label: change.action };
  const shares = change.quantityChangePct == null ? null : `shares ${signedPct(change.quantityChangePct, 1)}`;
  const weight = `weight ${pct(change.prevWeight, 2)} → ${pct(change.currWeight, 2)}`;
  const window = period.fromDate ? `traded between ${period.fromDate} and ${period.asOfDate}` : null;
  return {
    date: period.asOfDate,
    level: isBuy(change.action) ? 'good' : 'bad',
    label: [action.label, shares, weight, window].filter(Boolean).join(' · '),
  };
}

/** Comma or space separated symbols → upper-case NSE symbols, each once. */
export const parseSymbols = (text) => [...new Set(String(text || '')
  .split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean)
  .map((s) => (s.includes('.') ? s : `${s}.NSE`)))];
